import React, { useContext } from 'react'
import classNames from 'classnames'
import SVG from 'react-inlinesvg'
import { TicketsAppContext } from '../context/tickets-app-context'
import { OldCartContext } from '../context/old-cart-context'
import CartSummaryStepHeader from './cart-summary-step-header'
import DateFormat from '../../shared/helpers/date-format'
import { LoadingIcon } from '../../shared/components'
import cartIcon from 'images/cart-no-badge.svg' // in app/assets
import { STEPS } from '../helpers/steps'
import { has } from 'lodash-es'
import {
  hasMemberTickets,
  inManualUpdate,
  isChildLearning
} from '../helpers/logic'
import CurrentCartSummary from './current-cart-summary'
import { safeApiCall } from '../../services/api'

const { fancyFull } = DateFormat

const formatDate = date => {
  if (date) {
    return fancyFull(date)
  }
}

function CartSummary() {
  let USDollarFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  })

  const summaryTickets = chosenEvent => {
    if (!chosenEvent) return
    const currentTickets = chosenEvent.tickets.filter(
      ticket => ticket.quantity !== 0
    )
    const sortedByLabel = currentTickets,
      labelOrder = [
        'Employee Guest',
        'Adult Members',
        'Household Children',
        'Non-Residents',
        'Chicago Residents',
        'Illinois Residents'
      ],
      labels = labelOrder.reduce((r, k, v) => Object.assign(r, { [k]: v }), {})
    sortedByLabel.sort((a, b) => labels[a.label] - labels[b.label])

    const sortedByAgeGroup = sortedByLabel,
      ageOrder = [
        'employee',
        'adult',
        'adult2',
        'youth',
        'youth2',
        'child',
        'child2',
        'infant'
      ],
      ages = ageOrder.reduce((r, k, v) => Object.assign(r, { [k]: v }), {})
    sortedByAgeGroup.sort((a, b) => ages[a.ageGroup] - ages[b.ageGroup])

    return sortedByAgeGroup.map((ticket, index) => {
      let label = `${ticket.quantity} ${ticket.subLabel}`
      if (ticket.label.toLowerCase().includes('chicago')) {
        label = `${ticket.quantity} ${ticket.subLabel}-Chicago`
      }
      if (ticket.label.toLowerCase().includes('illinois')) {
        label = `${ticket.quantity} ${ticket.subLabel}-Illinois`
      }
      if (ticket.label.toLowerCase().includes('members')) {
        label = `${ticket.quantity} ${ticket.subLabel}-Members`
      }
      if (ticket.label.toLowerCase().includes('household children')) {
        label = `${ticket.quantity} ${ticket.subLabel}-Household`
      }
      if (ticket.label.toLowerCase().includes('employee guest')) {
        label = `${ticket.quantity} ${ticket.subLabel}-Employee Guest`
      }
      return <li className="small-paragraph my-0" key={`summaryTicket-${index}`}>{label}</li>
    })
  }

  const hasTickets = chosenEvent => {
    if (!chosenEvent) return false
    return chosenEvent.tickets.some(ticket => ticket.quantity !== 0)
  }

  const {
    actions: { setStep, goToCart },
    step,
    selectedDate,
    chosenEvent,
    fromEvent,
    loading,
    cart
  } = useContext(TicketsAppContext)

  const oldCart = useContext(OldCartContext)
  const fromEventAndNoDate = !selectedDate && fromEvent !== null
  let memberPricingTicketsExist = true
  const addOnsUpdated =
    inManualUpdate &&
    oldCart.addOnItemIds.every(addOnId => {
      let cartAddOn = cart.items.find(item => item.externalId === addOnId)
      memberPricingTicketsExist = cartAddOn
        ? has(cartAddOn.pricing, 'member')
        : true
      if (
        cartAddOn &&
        isChildLearning(cartAddOn.pricing.member) &&
        !cart.membership.childrenAllowed
      ) {
        return true
      } else {
        return cartAddOn && hasMemberTickets(cartAddOn.tickets)
      }
    })
  const disabledCartButton =
    inManualUpdate && !addOnsUpdated && memberPricingTicketsExist
  const buttonClassNames = classNames('button -big md:mt-32 w-full flex', {
    '-disabled': disabledCartButton
  })
  const noEdit = fromEvent !== null || inManualUpdate
  const abortExchange = async () => {
    const exchangeUrl = `/exchanges/${cart.exchangeDetails.exchangeId}`

    try {
      await safeApiCall('delete', exchangeUrl, 'Exchange could not be aborted')
    } catch (e) {
      console.log(e)
    }

    // always redirect back to order history because there is nothing else we can do and an error should have been logged
    window.location.href = `/order_history/${cart.exchangeDetails.relatedOrder}`
  }
  return (
    <div>
      <div
        className="bg-white md:shadow md:p-24"
        data-testid="cart-summary"
      >
        <div className="hidden md:block">
          <h2>Your Reservation</h2>
          {loading ? (
            <LoadingIcon visible />
          ) : (
            <React.Fragment>
              <div className="pl-12 pb-24 relative">
                <CartSummaryStepHeader
                  currentStep={step}
                  targetStep={STEPS['DATETICKETS']}
                  setStep={setStep}
                  title="Date & Time"
                  disable={fromEventAndNoDate}
                  noEdit={noEdit}
                />

                {selectedDate && step > STEPS['DATETICKETS'] && (
                  <p className="small-paragraph mb-0">
                    {formatDate(selectedDate)}
                  </p>
                )}
              </div>

              <div className="pl-12 pb-24 relative">
                <CartSummaryStepHeader
                  currentStep={step}
                  targetStep={STEPS['EVENTS']}
                  setStep={setStep}
                  title="Tickets"
                  noEdit={noEdit}
                  disable={fromEventAndNoDate}
                />

                {chosenEvent && step > STEPS['EVENTS'] && (
                  <ul className="mt-8 leading-normal text-16">
                    <li className="small-paragraph mb-0">
                      {!fromEventAndNoDate && chosenEvent
                        ? chosenEvent.name
                        : ''}
                    </li>
                  </ul>
                )}
              </div>

              <div className="pl-12 pb-24 relative">
                <CartSummaryStepHeader
                  last
                  currentStep={step}
                  targetStep={STEPS['PARTY']}
                  setStep={setStep}
                  title="Guests"
                  noEdit={fromEvent !== null}
                  disable={fromEventAndNoDate}
                />

                {hasTickets(chosenEvent) && step > STEPS['PARTY'] && (
                  <ul className="mt-8 leading-normal text-16">
                    {summaryTickets(chosenEvent)}
                  </ul>
                )}
              </div>
            </React.Fragment>
          )}
        </div>

        {step == 4 && (
          <div className="bg-white bottom-0 inset-x-0 fixed md:relative p-12 md:p-0 shadow-drawer md:shadow-none">
            <button
              className={buttonClassNames}
              onClick={goToCart}
              disabled={disabledCartButton}
              data-testid={`continue-to-cart`}
            >
              <SVG
                src={cartIcon}
                width="24"
                height="24"
                className="mr-8"
                aria-label="cart"
                aria-hidden="true"
              />
              <span>Continue to Cart</span>
            </button>
          </div>
        )}
      </div>
      <div
        className="bg-white mt-0 md:mt-12 md:shadow md:p-24"
        data-testid="current-cart-summary"
      >
        <h3 className="hidden md:block">
          Currently in Cart
        </h3>
        <CurrentCartSummary items={cart.items} />
      </div>

      {cart.exchangeDetails && (
        <div
          className="exchange-summary"
          data-testid="current-exchange"
        >
          <div>
            <p className="mt-0 text-16 mb-12">
              You have a{' '}
              {USDollarFormatter.format(
                Math.abs(cart.exchangeDetails.exchangeAmount)
              )}{' '}
              credit from Order #{cart.exchangeDetails.relatedOrder}.
            </p>

            <p className="m-0">
              <button
                data-testid={'btnAbortExchange'}
                className="text-depths underline text-14"
                onClick={() => abortExchange()}
              >
                Return to original order.
              </button>
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default CartSummary
